// Anonymous landing page (`/`). Perf-critical: this is the route Lighthouse
// CI scores, so the above-the-fold shell must stay free of framer-motion —
// the hero record demo is a static mock (no animation at all) and everything
// below the fold lives in LandingSections.tsx behind React.lazy (that chunk
// carries vendor-motion).

import { lazy, Suspense } from "react";
import { Link } from "react-router";
import { SkipToContent } from "@/components/SkipToContent";
import { Wordmark } from "@/components/Wordmark";

const LandingSections = lazy(() =>
  import("./LandingSections").then((m) => ({ default: m.LandingSections })),
);

/* ── Record demo (static, CSS-only — no framer-motion in the critical path) ─ */

function LandingRecordDemo() {
  return (
    <div className="py-6 select-none" aria-hidden="true">
      {/* Vault record card */}
      <div className="relative max-w-xs mx-auto rounded-2xl bg-white dark:bg-surface-800 border border-surface-200 dark:border-surface-700 shadow-xl shadow-surface-200/60 dark:shadow-surface-950/60 px-4 py-4 text-left">
        {/* Brand accent stripe */}
        <div className="absolute inset-x-0 top-0 h-1 rounded-t-2xl bg-gradient-to-r from-brand-300 via-brand-500 to-brand-300" />

        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-semibold text-surface-900 dark:text-surface-100">
            Skills Vault
          </span>
          <span className="text-[10px] text-surface-400">12 skills &middot; 8 stories</span>
        </div>

        {/* Skill rows with evidence */}
        <div className="space-y-2">
          <div className="rounded-lg bg-surface-50 dark:bg-surface-900 border border-surface-200 dark:border-surface-700 px-3 py-2 flex items-center justify-between">
            <span className="text-xs font-semibold text-surface-900 dark:text-surface-100">
              System Design
            </span>
            <span className="flex items-center gap-1.5">
              <span className="px-1.5 py-0.5 rounded text-[9px] font-medium bg-emerald-50 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400">
                3 stories
              </span>
              <span className="text-[10px] text-surface-400">expert</span>
            </span>
          </div>
          <div className="rounded-lg bg-surface-50 dark:bg-surface-900 border border-surface-200 dark:border-surface-700 px-3 py-2 flex items-center justify-between">
            <span className="text-xs font-semibold text-surface-900 dark:text-surface-100">
              Incident Response
            </span>
            <span className="flex items-center gap-1.5">
              <span className="px-1.5 py-0.5 rounded text-[9px] font-medium bg-emerald-50 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400">
                2 stories
              </span>
              <span className="text-[10px] text-surface-400">proficient</span>
            </span>
          </div>
        </div>
      </div>

      {/* An agent reading the record over MCP */}
      <div className="max-w-xs mx-auto mt-3 rounded-lg bg-surface-900 dark:bg-black px-3 py-2 text-left shadow-md">
        <p className="text-[10px] font-mono text-surface-300">
          claude &gt; search_skills(&quot;system design&quot;)
        </p>
        <p className="text-[10px] font-mono text-emerald-400">
          &rarr; expert &middot; 3 stories &middot; 2 experiences
        </p>
      </div>
    </div>
  );
}

/* ── Main landing page ───────────────────────────────────────────────── */

export function LandingPage() {
  return (
    <div className="min-h-screen bg-[var(--color-bg-page)] flex flex-col">
      <SkipToContent />
      {/* Nav */}
      <header className="border-b border-surface-200 dark:border-surface-700 bg-white/80 dark:bg-surface-900/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-5xl 2xl:max-w-6xl mx-auto px-6 2xl:px-8 h-16 flex items-center justify-between">
          <Wordmark animate="draw" hoverAnimate />
          <nav aria-label="Main" className="flex items-center gap-3">
            <Link
              to="/auth/login"
              className="px-4 py-2 text-sm font-medium text-surface-600 dark:text-surface-300 hover:text-surface-900 dark:hover:text-surface-100 rounded-lg hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors duration-300 ease-signature focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
            >
              Log in
            </Link>
            <Link
              to="/auth/signup"
              className="px-4 py-2 text-sm font-semibold text-white bg-brand-500 hover:bg-brand-600 rounded-lg transition-colors duration-300 ease-signature focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2"
            >
              Sign up free
            </Link>
          </nav>
        </div>
      </header>

      <main id="main-content" className="flex-1">
        {/* Hero */}
        <section
          className="max-w-5xl 2xl:max-w-6xl mx-auto px-6 2xl:px-8 pt-16 pb-12 text-center"
          aria-labelledby="hero-heading"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-brand-50 dark:bg-brand-950 border border-brand-200 dark:border-brand-800 text-brand-700 dark:text-brand-300 text-xs font-semibold mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-brand-500" aria-hidden="true" />
            Your professional record, evidenced
          </div>

          {/* Entrance choreography is pure CSS (animate-* tokens in index.css)
              — this route must stay framer-motion-free above the fold. The h1
              unmasks immediately; the lines below stagger in behind it. */}
          <h1
            id="hero-heading"
            className="text-4xl sm:text-5xl font-bold text-surface-900 dark:text-surface-50 mb-5 leading-tight font-display animate-hero-clip-reveal"
          >
            You&apos;re more qualified than your resume says. <br className="hidden sm:block" />
            <span className="inline-block text-brand-500 animate-fade-up-soft-1">
              Traitprint proves it.
            </span>
          </h1>

          <p className="text-lg text-surface-500 dark:text-surface-400 max-w-2xl mx-auto mb-4 leading-relaxed animate-fade-up-soft-2">
            A structured record of your skills, stories, and experience — with the evidence behind
            every claim. Connect ChatGPT, Claude, or Cursor and your AI can draft applications, prep
            interviews, and cite your actual evidence — from your record, not your resume.
          </p>
          <p className="text-sm font-medium text-brand-600 dark:text-brand-400 mb-8 animate-fade-up-soft-3">
            Try free — no API key needed
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-10 animate-fade-up-soft-3">
            <Link
              to="/auth/signup"
              className="w-full sm:w-auto px-6 py-3 text-base font-semibold text-white bg-brand-500 hover:bg-brand-600 rounded-xl transition-colors duration-300 ease-signature focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 shadow-sm"
            >
              Get started for free
            </Link>
            <Link
              to="/auth/login"
              className="w-full sm:w-auto px-6 py-3 text-base font-medium text-surface-700 dark:text-surface-200 bg-white dark:bg-surface-800 hover:bg-surface-50 dark:hover:bg-surface-700 border border-surface-200 dark:border-surface-700 rounded-xl transition-colors duration-300 ease-signature focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
            >
              Already have an account
            </Link>
          </div>

          {/* Record + agent illustration */}
          <div className="max-w-sm mx-auto">
            <LandingRecordDemo />
            <p className="text-xs text-surface-400 mt-2">
              Evidence attached to every claim — and your agents can query it
            </p>
          </div>
        </section>

        {/* Below the fold: narrative strip, previews, features, social
            proof, CTA — lazy chunk (carries framer-motion). The fallback
            reserves space so the footer doesn't jump into the viewport. */}
        <Suspense fallback={<div aria-hidden="true" className="min-h-screen" />}>
          <LandingSections />
        </Suspense>
      </main>

      {/* Footer */}
      <footer className="border-t border-surface-200 dark:border-surface-700 bg-white dark:bg-surface-900">
        <div className="max-w-5xl 2xl:max-w-6xl mx-auto px-6 2xl:px-8 py-6 pb-[calc(1.5rem_+_env(safe-area-inset-bottom,_0px))] flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-surface-400">
          <Wordmark className="opacity-60" />
          <nav
            aria-label="Footer"
            className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2"
          >
            <Link
              to="/trust"
              className="hover:text-surface-600 dark:hover:text-surface-200 transition-colors duration-300 ease-signature focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded"
            >
              Trust
            </Link>
            <Link
              to="/terms"
              className="hover:text-surface-600 dark:hover:text-surface-200 transition-colors duration-300 ease-signature focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded"
            >
              Terms
            </Link>
            <Link
              to="/privacy-public"
              className="hover:text-surface-600 dark:hover:text-surface-200 transition-colors duration-300 ease-signature focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded"
            >
              Privacy
            </Link>
            <Link
              to="/sub-processors"
              className="hover:text-surface-600 dark:hover:text-surface-200 transition-colors duration-300 ease-signature focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded"
            >
              Sub-Processors
            </Link>
            <Link
              to="/takedown"
              className="hover:text-surface-600 dark:hover:text-surface-200 transition-colors duration-300 ease-signature focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded"
            >
              Takedown
            </Link>
            <Link
              to="/auth/login"
              className="hover:text-surface-600 dark:hover:text-surface-200 transition-colors duration-300 ease-signature focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded"
            >
              Log in
            </Link>
          </nav>
        </div>
      </footer>
    </div>
  );
}
